/**
 * PageBeneficiary — V2 Beneficiary Detail Page
 *
 * Replaces the V1 Beneficiary detail page with V2 design system components.
 * Tabs: Details, Documents, Users
 */

// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { alpha, Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';

// icons
import {
	CloudUpload as IconUploadDocuments,
	DoNotDisturb as IconDeactivate,
	Edit as IconEdit,
	ModeEditOutlined as IconModeEdit,
	People as IconBeneficiary,
	PersonAdd as IconAddUser,
	PlayArrow as IconActivate,
} from '@mui/icons-material';

// services
import { fetchAiDocuments } from '../../../services/aiApi';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// v2 components
import { ColumnDef, DataTable, RowAction } from '../../ui/DataTable/DataTable';
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// dialogs
import { DialogAddUser } from '../../dialogs/DialogAddUser/DialogAddUser';
import { DialogDocumentViewer } from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogEditBeneficiary } from '../../dialogs/DialogEditBeneficiary/DialogEditBeneficiary';
import { DialogEditUser } from '../../dialogs/DialogEditUser/DialogEditUser';
import { DialogUpdateEntityStatus } from '../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUpdateUserStatus } from '../../dialogs/DialogUpdateUserStatus/DialogUpdateUserStatus';
import { DialogUploadDocument } from '../../dialogs/DialogUploadDocument/DialogUploadDocument';

// types
import { Entity, Loan, Pagination, User } from '../../../@types';
import type {
	AiDocument,
	AiDocumentUser,
} from '../ProjectV2/Documents/documentTypes';
import { formatDate } from '../../../utils/dates';

// Helpers

/** Infer DialogDocumentViewer file type from filename extension. */
const inferViewerFileType = (filename: string): 'pdf' | 'doc' | 'image' | 'xlsx' => {
	const ext = (filename.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'pdf';
	if (['xls', 'xlsx', 'csv'].includes(ext)) return 'xlsx';
	if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff'].includes(ext)) return 'image';
	return 'doc';
};

/** Format a byte count as a human-readable size for the viewer sidebar. */
const formatBytes = (bytes: number | null | undefined): string => {
	if (!bytes || bytes <= 0) return '—';
	const units = ['B', 'KB', 'MB', 'GB'];
	const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
	return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

/** Map an AiDocument to the minimal ContractDocument shape DialogDocumentViewer expects. */
const aiDocToContractDoc = (doc: AiDocument) => ({
	id: doc.id,
	name: doc.filename,
	category: 'other' as const,
	type: inferViewerFileType(doc.filename),
	size: formatBytes(((doc.metadata?.file_size_bytes as number) || 0) as number),
	uploadedAt: doc.uploaded_at,
	uploadedBy:
		doc.uploaded_by && typeof doc.uploaded_by === 'object'
			? (doc.uploaded_by as AiDocumentUser).displayName ||
				(doc.uploaded_by as AiDocumentUser).email ||
				'—'
			: '—',
});

/** Map an AiDocument to ViewerDocumentInfo (rich sidebar metadata). */
const aiDocToViewerInfo = (doc: AiDocument) => ({
	id: doc.id,
	name: doc.filename,
	tags: (doc.metadata?.tags as string[]) || [],
	sourceType: (doc.metadata?.source_type as string) || undefined,
	size: formatBytes(((doc.metadata?.file_size_bytes as number) || 0) as number),
	uploadedAt: doc.uploaded_at,
	uploadedBy:
		doc.uploaded_by && typeof doc.uploaded_by === 'object'
			? (doc.uploaded_by as AiDocumentUser).displayName ||
				(doc.uploaded_by as AiDocumentUser).email ||
				undefined
			: undefined,
	jobId: doc.job_id || undefined,
});

const getEntityStatusConfig = (status: string) => {
	switch (status) {
		case 'ACTIVE':
			return { color: '#16a34a', label: 'Active' };
		case 'INACTIVE':
			return { color: '#dc2626', label: 'Inactive' };
		case 'PENDING':
			return { color: '#d97706', label: 'Pending' };
		default:
			return { color: '#6b7280', label: status || 'Active' };
	}
};

// Component

export const PageBeneficiaryV2 = () => {
	const { beneficiaryId } = useParams<{ beneficiaryId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// tab state — synced to ?tab=users|documents so browser back/forward navigates tabs
	const tabParam = searchParams.get('tab');
	const activeTab: 'users' | 'documents' = tabParam === 'documents' ? 'documents' : 'users';
	const setActiveTab = (value: 'users' | 'documents') => {
		setSearchParams(
			(params) => {
				if (value === 'users') {
					params.delete('tab');
				} else {
					params.set('tab', value);
				}
				return params;
			},
			{ replace: false }
		);
	};

	// entity state
	const [beneficiary, setBeneficiary] = useState<Entity | null>(null);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
	const [showUploadDocumentsDialog, setShowUploadDocumentsDialog] = useState(false);

	// user/doc action state
	const [editUser, setEditUser] = useState<User | null>(null);
	const [deactivateUser, setDeactivateUser] = useState<User | null>(null);

	// documents state
	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

	// document viewer (URL-driven via ?doc_id)
	const viewerDocId = searchParams.get('doc_id');
	const viewerDocIndex = useMemo(
		() => (viewerDocId ? documents.findIndex((d) => d.id === viewerDocId) : -1),
		[viewerDocId, documents]
	);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>({
		page: 1,
		perPage: 10,
		total: 0,
		totalPages: 0,
	});
	const [searchDocuments, setSearchDocuments] = useState('');

	// users state
	const [users, setUsers] = useState<User[]>([]);
	const [paginationUsers, setPaginationUsers] = useState<Pagination>({
		page: 1,
		perPage: 10,
		total: 0,
		totalPages: 0,
	});
	const [searchUsers, setSearchUsers] = useState('');

	const openAddUserDialog = () => {
		setSearchParams((params) => {
			params.set('dialog', 'addUser');
			return params;
		});
	};

	const closeAddUserDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			return params;
		});
	};

	const headers = useMemo(
		() => ({
			Authorization: `Bearer ${accessToken}`,
			User: String(idToken),
		}),
		[accessToken, idToken]
	);

	// Fetch beneficiary
	const { isLoading: isLoadingBeneficiary, refetch: refetchBeneficiary } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/entities/${beneficiaryId}?project=${project?.id}&populate=wallet`,
		options: { headers },
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiary(res.data[0] || res.data);
			}
		},
	});

	// Fetch loans
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${beneficiaryId}&populate=entities`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) {
				setLoans(res.data);
			}
		},
	});

	// Fetch documents from bldt-ai-fn, scoped by tag `beneficiary:<id>`
	const refetchDocuments = useCallback(async () => {
		if (!project?.id || !beneficiaryId) return;
		setIsLoadingDocuments(true);
		try {
			const res = await fetchAiDocuments({
				project_id: project.id,
				tags: `beneficiary:${beneficiaryId}`,
				search: searchDocuments || undefined,
				page: paginationDocuments.page,
				page_size: paginationDocuments.perPage,
				populate: 'uploaded_by',
				order_by: 'uploaded_at',
				order: 'DESC',
			});
			setDocuments(res.data || []);
			setPaginationDocuments((prev) => ({
				...prev,
				total: res.pagination?.total_count ?? 0,
				totalPages: res.pagination?.total_pages ?? 0,
			}));
		} catch {
			setDocuments([]);
		} finally {
			setIsLoadingDocuments(false);
		}
	}, [project?.id, beneficiaryId, searchDocuments, paginationDocuments.page, paginationDocuments.perPage]);

	useEffect(() => {
		refetchDocuments();
	}, [refetchDocuments]);

	// Fetch users
	const { isLoading: isLoadingUsers, refetch: refetchUsers } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/users?entities=${beneficiaryId}&populate=entities,country&page=${paginationUsers.page}&limit=${paginationUsers.perPage}${searchUsers ? `&q=${searchUsers}` : ''}`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) {
				setUsers(res.data);
			}
			if (res?.pagination) {
				setPaginationUsers((prev) => ({
					...prev,
					total: res.pagination.total,
					totalPages: res.pagination.totalPages,
				}));
			}
		},
	});

	// Custom field definitions
	const customFieldDefs = useMemo(
		() =>
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'beneficiary')
				?.definitions || [],
		[project]
	);

	// Custom field stats
	const customFieldStats = useMemo(
		() => customFieldsToStats(customFieldDefs, beneficiary?.customData || {}),
		[customFieldDefs, beneficiary]
	);

	// Associated parent entities from loans
	const associatedEntities = useMemo(() => {
		const entities: { id: number; name: string }[] = [];
		loans.forEach((loan) => {
			const loanEntities = (loan as { entities?: { id?: number; name?: string; type?: string }[] })
				.entities;
			if (Array.isArray(loanEntities)) {
				loanEntities.forEach((e) => {
					if (e && e.id && e.type !== 'BENEFICIARY' && !entities.find((x) => x.id === e.id)) {
						entities.push({ id: e.id, name: e.name || `Entity #${e.id}` });
					}
				});
			}
		});
		return entities;
	}, [loans]);

	// Build header stats
	const walletStatus = beneficiary?.wallet?.status || 'ACTIVE';
	const statusConfig = getEntityStatusConfig(walletStatus);

	const headerStats = beneficiary
		? [
				{
					label: 'Status',
					value: (
						<Chip
							label={statusConfig.label}
							size="small"
							sx={{
								bgcolor: alpha(statusConfig.color, 0.1),
								color: statusConfig.color,
								fontSize: '1rem',
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				{
					label: 'Type',
					value: beneficiary.subtype || '—',
				},
				...(beneficiary.parent && typeof beneficiary.parent !== 'number'
					? [{ label: 'Parent Entity', value: beneficiary.parent.name }]
					: []),
				...(beneficiary.idExternal
					? [{ label: 'External ID', value: beneficiary.idExternal }]
					: []),
				...(beneficiary.wallet?.address
					? [
							{
								label: 'Wallet',
								value: `${beneficiary.wallet.address.slice(0, 10)}…`,
							},
						]
					: []),
				{
					label: 'KYC Verified',
					value: beneficiary.isKycVerified ? 'Yes' : 'No',
				},
				...(associatedEntities.length > 0
					? [{ label: 'Associated Entities', value: `${associatedEntities.length}` }]
					: []),
				...(loans.length > 0 ? [{ label: 'Financing Sources', value: `${loans.length}` }] : []),
				{
					label: 'Created',
					value: formatDate({ date: beneficiary.created }),
				},
				{
					label: 'Last Updated',
					value: formatDate({ date: beneficiary.modified }),
				},
				// Custom fields as inline stats
				...customFieldStats,
			]
		: [];

	// User row actions
	const userRowActions: RowAction<User>[] = useMemo(
		() => [
			{
				label: 'Edit User',
				icon: <IconModeEdit fontSize="small" />,
				onClick: (user: User) => setEditUser(user),
			},
			{
				label: 'Deactivate User',
				icon: <IconDeactivate fontSize="small" />,
				onClick: (user: User) => {
					setSearchParams((params) => {
						params.set('dialogStatus', 'DEACTIVATED');
						return params;
					});
					setDeactivateUser(user);
				},
				destructive: true,
				dividerBefore: true,
				show: (user: User) => user.status !== 'DEACTIVATED',
			},
			{
				label: 'Activate User',
				icon: <IconActivate fontSize="small" />,
				onClick: (user: User) => {
					setSearchParams((params) => {
						params.set('dialogStatus', 'INVITED');
						return params;
					});
					setDeactivateUser(user);
				},
				dividerBefore: true,
				show: (user: User) => user.status === 'DEACTIVATED',
			},
		],
		[setSearchParams]
	);

	// Document columns (AiDocument from bldt-ai-fn get_documents)
	const documentColumns: ColumnDef<AiDocument>[] = useMemo(
		() => [
			{
				id: 'filename',
				label: 'Name',
				minWidth: 200,
				render: (doc) => (
					<Stack>
						<Typography variant="body2" fontWeight={600}>
							{doc.filename}
						</Typography>
						{doc.doc_type && (
							<Typography variant="caption" color="text.secondary">
								{doc.doc_type}
							</Typography>
						)}
					</Stack>
				),
			},
			{
				id: 'doc_type',
				label: 'Type',
				minWidth: 120,
				render: (doc) => doc.doc_type || '—',
			},
			{
				id: 'uploaded_by',
				label: 'Uploaded By',
				minWidth: 140,
				render: (doc) => {
					const u = doc.uploaded_by;
					if (u && typeof u === 'object') {
						const user = u as AiDocumentUser;
						return user.displayName || user.email || '—';
					}
					return '—';
				},
			},
			{
				id: 'uploaded_at',
				label: 'Uploaded',
				minWidth: 120,
				sortable: true,
				render: (doc) => formatDate({ date: doc.uploaded_at }),
			},
		],
		[]
	);

	// User columns
	const userColumns: ColumnDef<User>[] = useMemo(
		() => [
			{
				id: 'name',
				label: 'Name',
				minWidth: 180,
				render: (user) => (
					<Stack>
						<Typography variant="body2" fontWeight={600}>
							{user.fullName || `${user.firstName} ${user.lastName}`}
						</Typography>
						{user.title && (
							<Typography variant="caption" color="text.secondary">
								{user.title}
							</Typography>
						)}
					</Stack>
				),
			},
			{
				id: 'email',
				label: 'Email',
				minWidth: 200,
				render: (user) => user.email || '—',
			},
			{
				id: 'role',
				label: 'Role',
				minWidth: 120,
				render: (user) => user.role || '—',
			},
			{
				id: 'status',
				label: 'Status',
				minWidth: 100,
				render: (user) => {
					const isActive = user.status === 'ACTIVE';
					return (
						<Chip
							label={user.status || 'Active'}
							size="small"
							sx={{
								bgcolor: alpha(isActive ? '#16a34a' : '#6b7280', 0.1),
								color: isActive ? '#16a34a' : '#6b7280',
								fontWeight: 600,
							}}
						/>
					);
				},
			},
			{
				id: 'modified',
				label: 'Last Updated',
				minWidth: 120,
				sortable: true,
				render: (user) => formatDate({ date: user.modified }),
			},
		],
		[]
	);

	// Not found
	if (!isLoadingBeneficiary && !beneficiary) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>Beneficiary not found</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{/* Header */}
			<HeaderDetailPage
				loading={isLoadingBeneficiary}
				icon={IconBeneficiary}
				breadcrumb={{
					label: 'Beneficiaries',
					to: `/project/${project?.idWbg}/spend-plans/beneficiaries`,
				}}
				title={beneficiary?.name || ''}
				variant="contract"
				stats={headerStats}
				customAction={
					beneficiary ? (
						<BtnSplit
							actions={[
								{
									children: 'Edit',
									startIcon: <IconEdit />,
									onClick: () => {
										setSearchParams((params) => {
											params.set('dialog', 'editBeneficiary');
											params.set('dialogBeneficiary', String(beneficiary.id));
											return params;
										});
									},
								},
								...(activeTab === 'users'
									? [
											{
												children: 'Add User',
												startIcon: <IconAddUser />,
												onClick: openAddUserDialog,
											},
										]
									: []),
								...(activeTab === 'documents'
									? [
											{
												children: 'Upload Documents',
												startIcon: <IconUploadDocuments />,
												onClick: () => setShowUploadDocumentsDialog(true),
											},
										]
									: []),
								{
									children: beneficiary.wallet?.status === 'INACTIVE' ? 'Activate' : 'Deactivate',
									startIcon:
										beneficiary.wallet?.status === 'INACTIVE' ? (
											<IconActivate />
										) : (
											<IconDeactivate />
										),
									onClick: () => setShowDeactivateDialog(true),
								},
							]}
						/>
					) : undefined
				}
			/>

			{/* Tabs */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 1,
				}}>
				<Tabs
					value={activeTab}
					onChange={(_, value) => setActiveTab(value)}
					variant="standard"
					sx={{ flex: 1, minHeight: 40 }}>
					<Tab
						label="Users"
						value="users"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
					<Tab
						label="Documents"
						value="documents"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
				</Tabs>
			</Box>

			{/* Tab Content */}
			<Box sx={{ px: 3, pb: 3, pt: 2 }}>
				{/* ============== Documents Tab ============== */}
				{activeTab === 'documents' && (
					<DataTable<AiDocument>
						data={documents}
						columns={documentColumns}
						getRowKey={(doc) => doc.id}
						loading={isLoadingDocuments}
						onRowClick={(doc) => {
							setSearchParams(
								(params) => {
									params.set('doc_id', doc.id);
									return params;
								},
								{ replace: false }
							);
						}}
						searchValue={searchDocuments}
						onSearchChange={setSearchDocuments}
						searchPlaceholder="Search documents…"
						page={paginationDocuments.page}
						rowsPerPage={paginationDocuments.perPage}
						totalCount={paginationDocuments.total || 0}
						onPageChange={(p) => setPaginationDocuments((prev) => ({ ...prev, page: p }))}
						onRowsPerPageChange={(rpp) =>
							setPaginationDocuments((prev) => ({ ...prev, page: 1, perPage: rpp }))
						}
						emptyMessage="No documents"
						emptyContent="Upload documents to associate them with this beneficiary."
						emptyActions={[
							{
								children: 'Upload Documents',
								onClick: () => setShowUploadDocumentsDialog(true),
								variant: 'contained',
							},
						]}
					/>
				)}

				{/* ============== Users Tab ============== */}
				{activeTab === 'users' && (
					<DataTable<User>
						data={users}
						columns={userColumns}
						getRowKey={(user) => user.id}
						loading={isLoadingUsers}
						rowActions={userRowActions}
						searchValue={searchUsers}
						onSearchChange={setSearchUsers}
						searchPlaceholder="Search users…"
						page={paginationUsers.page}
						rowsPerPage={paginationUsers.perPage}
						totalCount={paginationUsers.total || 0}
						onPageChange={(p) => setPaginationUsers((prev) => ({ ...prev, page: p }))}
						onRowsPerPageChange={(rpp) =>
							setPaginationUsers((prev) => ({ ...prev, page: 1, perPage: rpp }))
						}
						emptyMessage="No users"
						emptyContent="Add users to grant them access to this beneficiary."
						emptyActions={[
							{
								children: 'Add User',
								onClick: openAddUserDialog,
								variant: 'contained',
							},
						]}
					/>
				)}
			</Box>

			{/* Edit Dialog */}
			<DialogEditBeneficiary
				isOpen={searchParams.get('dialog') === 'editBeneficiary'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						params.delete('dialogBeneficiary');
						return params;
					});
				}}
				onSuccess={() => {
					refetchBeneficiary();
				}}
			/>
			{beneficiary && showDeactivateDialog && (
				<DialogUpdateEntityStatus
					entity={beneficiary}
					isOpen={showDeactivateDialog}
					onClose={() => setShowDeactivateDialog(false)}
					onSuccess={() => {
						setShowDeactivateDialog(false);
						refetchBeneficiary();
					}}
				/>
			)}

			{beneficiary && (
				<DialogAddUser
					entity={beneficiary}
					isOpen={searchParams.get('dialog') === 'addUser'}
					onClose={closeAddUserDialog}
					onSuccess={() => {
						closeAddUserDialog();
						refetchUsers();
					}}
				/>
			)}

			{beneficiary && project && (
				<DialogUploadDocument
					isOpen={showUploadDocumentsDialog}
					onClose={() => setShowUploadDocumentsDialog(false)}
					projectId={project.id}
					sourceType="beneficiary"
					sourceId={beneficiary.id}
					onSuccess={() => {
						setShowUploadDocumentsDialog(false);
						refetchDocuments();
					}}
				/>
			)}

			{/* Document Viewer Dialog (bldt-ai-fn) */}
			{viewerDocIndex >= 0 && (
				<DialogDocumentViewer
					isOpen={!!viewerDocId}
					currentIndex={viewerDocIndex}
					documents={documents.map(aiDocToContractDoc)}
					blobFilenames={documents.map((d) => d.filename)}
					documentInfos={documents.map(aiDocToViewerInfo)}
					onClose={() => {
						setSearchParams(
							(params) => {
								params.delete('doc_id');
								return params;
							},
							{ replace: false }
						);
					}}
					onNavigate={(index) => {
						const next = documents[index];
						if (!next) return;
						setSearchParams(
							(params) => {
								params.set('doc_id', next.id);
								return params;
							},
							{ replace: true }
						);
					}}
				/>
			)}

			{/* Edit User Dialog */}
			{editUser && (
				<DialogEditUser
					frame="ENTITY"
					user={editUser}
					isOpen={!!editUser}
					onClose={() => setEditUser(null)}
					onSuccess={() => {
						setEditUser(null);
						refetchUsers();
					}}
				/>
			)}

			{/* Deactivate/Activate User Dialog */}
			{deactivateUser && (
				<DialogUpdateUserStatus
					frame="ENTITY"
					user={deactivateUser}
					isOpen={!!deactivateUser}
					onClose={() => {
						setDeactivateUser(null);
						setSearchParams((params) => {
							params.delete('dialogStatus');
							return params;
						});
					}}
					onSuccess={() => {
						setDeactivateUser(null);
						setSearchParams((params) => {
							params.delete('dialogStatus');
							return params;
						});
						refetchUsers();
					}}
				/>
			)}
		</Box>
	);
};
